/**
 * DEV/TEST ONLY — Pre-seeded payapp OCR results.
 *
 * How to use:
 * 1. Grab the raw OCR response from the network tab after any upload (POST /api/project/upload-payapp/...)
 * 2. Paste coverFields, table, headerMappings into an entry below
 * 3. Give it an id and displayName — project/draw are resolved at runtime from the open dialog
 *
 * Add up to 3 entries. Each needs a unique `id`.
 */

export interface RawPayappSeedEntry {
  id: string;
  displayName: string;
  coverFields: Record<string, string | null>;
  table: string[][];
  headerMappings?: Record<string, string>;
}

const SEEDED_PAYAPPS: RawPayappSeedEntry[] = [
  {
    id: 'seed-1',
    displayName: 'Buccees Payapp 1',
    coverFields: {
      AdjustmentsbyCDCC: null,
      AmountCertified: '$ 2,537,958.94',
      ApplicationNumber: '1',
      BalancetoCompletePlusRetainage: '$ 58,481,945.06',
      ContractBudgetSumtoDate: null,
      CurrentPaymentDue: '$ 2,537,958.94',
      Difference: null,
      EstRetainageReleasedThisPeriod: null,
      LessPreviousFundings: '$ 0.00',
      OriginalContractBudgetSum: '61,019,904.00',
      RetainagePercentage: '',
      ReallocationsorChangeOrders: null,
      RequestedAmount: null,
      TotalAmountReccomendedForFunding: null,
      TotalCompletedandStored: '$ 2,819,954.38',
      TotalContractBudgetPercentageComplete: null,
      TotalEarnedLessRetainage: '$ 2,537,958.94',
      TotalRetainageHeld: '$ 281,995.44',
    },
    table: [
      [
        'A ITEM NO.',
        'description',
        'revisedBudget',
        'previousApplication',
        'completeThisPeriod',
        'F MATERIALS PRESENTLY STORED (NOT IN D OR E)',
        'completedStoredToDate',
        '% (G / C)',
        'H BALANCE TO FINISH (C - G)',
        'retainage',
      ],
      ['', 'General Construction', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['001', 'Contract Amount', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['01 0000', 'GENERAL REQUIREMENTS', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['01 3000', 'Insurance', '420,935.00', '0.00', '420,935.00', '0.00', '420,935.00', '100.0%', '0.00', '42,093.50'],
      ['01 5000', 'Professional Services', '45,500.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '45,500.00', '0.00'],
      ['01 5005', 'Security Guard Services', '135,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '135,000.00', '0.00'],
      ['01 5010', 'SWPPP / Erosion Control', '247,323.00', '0.00', '65,000.00', '0.00', '65,000.00', '26.3%', '182,323.00', '6,500.00'],
      [
        '01 5015',
        'Temporary Project Expenses',
        '1,857,915.00',
        '0.00',
        '137,953.38',
        '0.00',
        '137,953.38',
        '7.4%',
        '1,719,961.62',
        '13,795.34',
      ],
      [
        '01 7500',
        'Cleaning, Pressure Washing (Continuous & Final)',
        '180,350.00',
        '0.00',
        '684.62',
        '0.00',
        '684.62',
        '0.4%',
        '179,665.38',
        '68.46',
      ],
      ['03 0000', 'CONCRETE', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      [
        '03 3000',
        'Slab, Grade Beams, Piers/Footings (Building & Enclosures)',
        '1,257,100.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '1,257,100.00',
        '0.00',
      ],
      ['03 3005', 'Fuel Canopy Footings', '250,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '250,000.00', '0.00'],
      ['03 3010', 'Paving Under Fuel Canopy', '725,550.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '725,550.00', '0.00'],
      ['03 3015', 'Paving Over Fuel Tanks', '90,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '90,000.00', '0.00'],
      [
        '03 3020',
        'All Other Site Paving Including Parking and Colored Concrete Bands',
        '4,681,076.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '4,681,076.00',
        '0.00',
      ],
      [
        'A ITEM NO.',
        'B DESCRIPTION OF WORK',
        'C SCHEDULED VALUE',
        'D WORK COMPLETED FROM PREVIOUS APPLICATION (D + E)',
        'E THIS PERIOD',
        'F MATERIALS PRESENTLY STORED (NOT IN DOR E)',
        'G TOTAL COMPLETED AND STORED TO DATE (D+E+F)',
        '% (G / C)',
        'H BALANCE TO FINISH (C - G)',
        'I RETAINAGE (IF VARIABLE RATE)',
      ],
      [
        '03 3025',
        'Curbs, Sidewalks incl. Grade Beams, Enclosures on Sidewalks, All Ramps, Statue Pedestals',
        '250,000.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '250,000.00',
        '0.00',
      ],
      ['03 3030', 'Light Pole Footings (Building)', '15,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '15,000.00', '0.00'],
      ['03 3035', 'Light Pole Footings (Site)', '80,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '80,000.00', '0.00'],
      ['03 3040', 'Bollards (Fuel Canopy Area)', '110,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '110,000.00', '0.00'],
      ['03 3045', 'Bollards (All Others)', '35,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '35,000.00', '0.00'],
      [
        '03 3050',
        'Miscellaneous Concrete Associated w/Storm Drainage',
        '45,000.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '45,000.00',
        '0.00',
      ],
      ['03 4713', 'Tilt Wall Fabrication & Erection', '755,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '755,000.00', '0.00'],
      ['04 0000', 'MASONRY', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['04 7200', 'Stone Work / Bullnose (Building)', '737,805.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '737,805.00', '0.00'],
      ['04 7205', 'Stone Work / Bullnose (Fuel Canopy)', '228,588.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '228,588.00', '0.00'],
      ['05 0000', 'METALS', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      [
        '05 1200',
        'Building Structural Steel & Erection (Including Trench Grating & Roof Ladder)',
        '1,587,592.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '1,587,592.00',
        '0.00',
      ],
      [
        '05 4000',
        'Cold-Formed Metal Stud Framing Interior/Exterior (Building)',
        '575,000.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '575,000.00',
        '0.00',
      ],
      ['05 4005', 'Cold-Formed Metal Stud Framing (Canopy)', '158,256.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '158,256.00', '0.00'],
      ['06 0000', 'WOOD, PLASTICS & COMPOSITES', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['06 0525', 'Slatwall Paneling System', '79,403.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '79,403.00', '0.00'],
      [
        '06 1000',
        'Fire Rated Plywood (Building Interior/Exterior)',
        '83,000.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '83,000.00',
        '0.00',
      ],
      ['06 1005', 'Fire Rated Plywood (Canopy)', '131,650.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '131,650.00', '0.00'],
      [
        '06 1015',
        'Shelving & Brackets, Clothes Rods & Brackets, PLAM Countertops & Brackets, Interior Wood Trim (Building)',
        '37,061.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '37,061.00',
        '0.00',
      ],
      [
        'A ITEM NO.',
        'B DESCRIPTION OF WORK',
        'C SCHEDULED VALUE',
        'D WORK COMPLETED FROM PREVIOUS APPLICATION (D + E)',
        'E THIS PERIOD',
        'F MATERIALS PRESENTLY STORED (NOT IN DOR E)',
        'G TOTAL COMPLETED AND STORED TO DATE (D+E+F)',
        '% (G / C)',
        'H BALANCE TO FINISH (C - G)',
        'I RETAINAGE (IF VARIABLE RATE)',
      ],
      [
        '06 4100',
        'Store Millwork & Solid Surface Countertops (Excludes Foodservice Millwork)',
        '544,646.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '544,646.00',
        '0.00',
      ],
      ['07 0000', 'THERMAL & MOISTURE PROTECTION', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['07 2400', 'Stucco/EIFS Moldings (Building)', '215,504.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '215,504.00', '0.00'],
      ['07 2405', 'Stucco/EIFS Moldings (Fuel Canopy)', '288,016.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '288,016.00', '0.00'],
      [
        '07 4000',
        'Roofing System (Single-Ply & Metal Roof Systems)',
        '873,203.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '873,203.00',
        '0.00',
      ],
      [
        '07 9200',
        'Interior/Exterior Caulking (Building & Site)',
        '386,525.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '386,525.00',
        '0.00',
      ],
      ['08 0000', 'OPENINGS', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      [
        '08 0671',
        'Interior/Exterior Finish Hardware, Locksmith Services Final Keying, Pad Locks for IDF Cabinets, Knox Boxes (Building)',
        '233,006.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '233,006.00',
        '0.00',
      ],
      ['08 3105', 'Access Doors (Fuel Canopies)', '9,500.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '9,500.00', '0.00'],
      ['08 3323', 'Overhead Coiling Doors (Building)', '207,214.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '207,214.00', '0.00'],
      ['08 4229', 'Automatic Sliding Glass Door w/Frame', '125,539.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '125,539.00', '0.00'],
      [
        '08 4313',
        'Interior Glass / Storefront Glass & Frames / Mirrors (Building)',
        '104,086.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '104,086.00',
        '0.00',
      ],
      ['09 0000', 'FINISHES', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      [
        '09 2116',
        'Drywall System / Cement Board / Sheathing (Building)',
        '1,475,000.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '1,475,000.00',
        '0.00',
      ],
      [
        '09 2117',
        'Drywall System / Cement Board / Sheathing (Canopy)',
        '165,000.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '165,000.00',
        '0.00',
      ],
      ['09 3000', 'Ceramic Tile System (Building)', '1,320,400.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '1,320,400.00', '0.00'],
      ['09 5100', 'Acoustical Ceiling System (Building)', '262,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '262,000.00', '0.00'],
      ['09 6119', 'Staining of Concrete Star', '85,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '85,000.00', '0.00'],
      [
        'A ITEM NO.',
        'B DESCRIPTION OF WORK',
        'C SCHEDULED VALUE',
        'D WORK COMPLETED FROM PREVIOUS APPLICATION (D + E)',
        'E THIS PERIOD',
        'F MATERIALS PRESENTLY STORED (NOT IN DOR E)',
        'G TOTAL COMPLETED AND STORED TO DATE (D+E+F)',
        '% (G / C)',
        'H BALANCE TO FINISH (C - G)',
        'I RETAINAGE (IF VARIABLE RATE)',
      ],
      [
        '09 9000',
        'Interior/Exterior/Site Painting - Including Doors, Roof Ladder, Manhole Covers at Fuel Tanks, Enclosure Gates, Access Doors (Building)',
        '114,942.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '114,942.00',
        '0.00',
      ],
      ['10 0000', 'SPECIALTIES', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      [
        '10 1400',
        'Signage Interior & Exterior (Building Logos, Channel Letters & Bird Spikes)',
        '47,397.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '47,397.00',
        '0.00',
      ],
      [
        '10 1405',
        'Signage (Fuel Canopy Logos, Channel Letters, Panel Signs & Bird Spikes)',
        '48,879.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '48,879.00',
        '0.00',
      ],
      ['10 1410', 'Pylon Sign Pole (materials & installation)', '264,258.15', '0.00', '0.00', '0.00', '0.00', '0.0%', '264,258.15', '0.00'],
      ['10 1415', 'Pylon Sign Logo (materials & installation)', '142,292.85', '0.00', '0.00', '0.00', '0.00', '0.0%', '142,292.85', '0.00'],
      ['10 2800', 'Grab Bars (Building)', '6,900.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '6,900.00', '0.00'],
      [
        '10 4400',
        'Fire Protection Specialties (Extinguishers & Cabinets)',
        '34,000.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '34,000.00',
        '0.00',
      ],
      ['10 7300', 'Fuel Canopies', '1,132,276.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '1,132,276.00', '0.00'],
      ['10 7305', 'LED Lighting for Fuel Canopies', '59,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '59,000.00', '0.00'],
      ['11 0000', 'EQUIPMENT', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['11 4000', 'Foodservice Equipment', '1,616,094.00', '0.00', '110,571.62', '0.00', '110,571.62', '6.8%', '1,505,522.38', '11,057.16'],
      [
        '11 4010',
        'Refrigeration Equipment',
        '1,256,941.00',
        '0.00',
        '137,641.90',
        '0.00',
        '137,641.90',
        '11.0%',
        '1,119,299.10',
        '13,764.19',
      ],
      ['21 0000', 'FIRE SUPRESSION', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['21 0100', 'Fire Sprinkler System', '661,685.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '661,685.00', '0.00'],
      ['22 0000', 'PLUMBING', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['22 0000', 'Sanitary Plumbing', '1,601,600.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '1,601,600.00', '0.00'],
      ['22 0005', 'Domestic Water Plumbing', '366,080.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '366,080.00', '0.00'],
      ['22 0010', 'Plumbing Fixtures', '160,160.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '160,160.00', '0.00'],
      ['22 0015', 'Gas Piping', '68,640.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '68,640.00', '0.00'],
      [
        'A ITEM NO.',
        'B DESCRIPTION OF WORK',
        'C SCHEDULED VALUE',
        'D WORK COMPLETED FROM PREVIOUS APPLICATION (D + E)',
        'E THIS PERIOD',
        'F MATERIALS PRESENTLY STORED (NOT IN DOR E)',
        'G TOTAL COMPLETED AND STORED TO DATE (D+E+F)',
        '% (G / C)',
        'H BALANCE TO FINISH (C - G)',
        'I RETAINAGE (IF VARIABLE RATE)',
      ],
      ['22 3116', 'Water Filtration Systems', '91,520.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '91,520.00', '0.00'],
      ['23 0000', 'HVAC', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['23 0000', 'HVAC Systems (Building)', '2,704,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '2,704,000.00', '0.00'],
      [
        '23 0933',
        'Building Management & Control Systems (BMCS)',
        '422,716.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '422,716.00',
        '0.00',
      ],
      ['26 0000', 'ELECTRICAL', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      [
        '26 0000',
        'Building Electrical (excludes Surge Protection, Foodservice Electrical and Lighting)',
        '3,163,261.32',
        '0.00',
        '40,000.00',
        '200,000.00',
        '240,000.00',
        '7.6%',
        '2,923,261,32',
        '24,000.00',
      ],
      ['26 0005', 'Surge Protection', '92,087.39', '0.00', '0.00', '0.00', '0.00', '0.0%', '92,087.39', '0.00'],
      ['26 0010', 'Interior Lighting', '465,896.42', '0.00', '0.00', '0.00', '0.00', '0.0%', '465,896.42', '0.00'],
      [
        '26 0015',
        'Exterior Lighting (Parking Lot & Sidewalks)',
        '260,539.94',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '260,539.94',
        '0.00',
      ],
      ['26 0020', 'Exterior Lighting (Wall Packs)', '22,460.34', '0.00', '0.00', '0.00', '0.00', '0.0%', '22,460.34', '0.00'],
      ['26 0025', 'Foodservice Electrical', '269,823.55', '0.00', '0.00', '0.00', '0.00', '0.0%', '269,823.55', '0.00'],
      ['26 0030', 'Site Electrical', '1,455,824.59', '0.00', '0.00', '50,000.00', '50,000.00', '3.4%', '1,405,824.59', '5,000.00'],
      ['26 0035', 'Fuel Canopy Electrical', '1,545,945.20', '0.00', '0.00', '25,000.00', '25,000.00', '1.6%', '1,520,945.20', '2,500.00'],
      ['26 0040', 'Fuel System Electrical', '139,029.50', '0.00', '0.00', '40,000.00', '40,000.00', '28.8%', '99,029.50', '4,000.00'],
      ['26 3100', 'Fire Alarm System', '121,911.75', '0.00', '0.00', '0.00', '0.00', '0.0%', '121,911.75', '0.00'],
      ['28 0000', 'LOW VOLTAGE', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['28 1800', 'Low Voltage', '1,287,180.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '1,287,180.00', '0.00'],
      ['31 0000', 'EARTHWORK', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      [
        '31 0000',
        'Earthwork, Clearing, Grading, Excavation, Trenching, Fill, Soil Stabilization',
        '6,965,385,00',
        '0.00',
        '1,269,000.00',
        '0.00',
        '1,269,000.00',
        '18.2%',
        '5,696,385.00',
        '126,900.00',
      ],
      ['32 0000', 'EXTERIOR IMPROVEMENTS', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      [
        'A ITEM NO.',
        'B DESCRIPTION OF WORK',
        'C SCHEDULED VALUE',
        'D WORK COMPLETED FROM PREVIOUS APPLICATION (D + E)',
        'E THIS PERIOD',
        'F MATERIALS PRESENTLY STORED (NOT IN DOR E)',
        'G TOTAL COMPLETED AND STORED TO DATE (D+E+F)',
        '% (G /C)',
        'H BALANCE TO FINISH (C - G)',
        'I RETAINAGE (IF VARIABLE RATE)',
      ],
      [
        '32 1713',
        'Parking Lot Striping, ADA Signage, Traffic Control Signage & Wheel Stops',
        '238,913.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '238,913.00',
        '0.00',
      ],
      ['32 3100', 'Site Fencing', '130,580.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '130,580.00', '0.00'],
      ['32 8000', 'Irrigation & Irrigation Backflow Device', '460,962.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '460,962.00', '0.00'],
      ['32 9000', 'Landscaping', '1,270,169.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '1,270,169.00', '0.00'],
      ['33 0000', 'Utilities', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['33 0000', 'Site Utilities', '4,189,391.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '4,189,391.00', '0.00'],
      ['50 0000', 'Fuel System', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['50 1000', 'Fuel System Installation & Start-Up', '2,481,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '2,481,000.00', '0.00'],
      ['70 0000', 'ALLOWANCES', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['70 0001', 'Drawing Revision Allowance', '250,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '250,000.00', '0.00'],
      ['71 0002', 'Undercut Deep Foundations Allowance', '178,124.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '178,124.00', '0.00'],
      [
        '71 0003',
        'Undercut Building Pad Allowance',
        '158,194.00',
        '0.00',
        '90,849.00',
        '0.00',
        '90,849.00',
        '57.4%',
        '67,345.00',
        '9,084.90',
      ],
      ['71 0004', 'Temp Dewatering Allowance', '50,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '50,000.00', '0.00'],
      ['71 0005', 'Temp Traffic Light Allowance', '200,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '200,000.00', '0.00'],
      ['71 0006', 'DAS System Testing', '13,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '13,000.00', '0.00'],
      ['72 0000', 'Buyout Tracking', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      [
        '80 0000',
        "Contractor's Monthly Fixed Fee & Expenses",
        '3,717,102.00',
        '0.00',
        '232,318.86',
        '0.00',
        '232,318.86',
        '6.2%',
        '3,484,783.14',
        '23,231.89',
      ],
      [
        '',
        'GRAND TOTAL',
        '$61,019,904.00',
        '$0.00',
        '$2,504,954.38',
        '$315,000.00',
        '$2,819,954.38',
        '4.6%',
        '$58,199,949.62',
        '$281,995.44',
      ],
    ],
    headerMappings: {
      'G TOTAL COMPLETED AND STORED TO DATE (D+E+F)': 'completedStoredToDate',
      'C SCHEDULED VALUE': 'revisedBudget',
      'I RETAINAGE (IF VARIABLE RATE)': 'retainage',
      'D WORK COMPLETED FROM PREVIOUS APPLICATION (D + E)': 'previousApplication',
      'B DESCRIPTION OF WORK': 'description',
      'E THIS PERIOD': 'completeThisPeriod',
    },
  },
  {
    id: 'seed-2',
    displayName: 'Buccees Payapp 2',
    coverFields: {
      AdjustmentsbyCDCC: null,
      AmountCertified: '$ 2,350,764.85',
      ApplicationNumber: '2',
      BalancetoCompletePlusRetainage: '$ 56,131,180.21',
      ContractBudgetSumtoDate: null,
      CurrentPaymentDue: '$ 2,350,764.85',
      Difference: null,
      EstRetainageReleasedThisPeriod: null,
      LessPreviousFundings: '$ 2,537,958.94',
      OriginalContractBudgetSum: '$ 61,019,904.00',
      RetainagePercentage: '',
      ReallocationsorChangeOrders: null,
      RequestedAmount: null,
      TotalAmountReccomendedForFunding: null,
      TotalCompletedandStored: '$ 5,431,915.32',
      TotalContractBudgetPercentageComplete: null,
      TotalEarnedLessRetainage: '$ 4,888,723.79',
      TotalRetainageHeld: '$ 543,191.53',
    },
    table: [
      [
        'A ITEM NO.',
        'description',
        'revisedBudget',
        'previousApplication',
        'completeThisPeriod',
        'storedThisPeriod',
        'completedStoredToDate',
        '% (G /C)',
        'H BALANCE TO FINISH (C - G)',
        'retainage',
      ],
      ['', 'General Construction', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['001', 'Contract Amount', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['01 0000', 'GENERAL REQUIREMENTS', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['01 3000', 'Insurance', '420,935.00', '420,935.00', '0.00', '0.00', '420,935.00', '100.0%', '0.00', '42,093.50'],
      ['01 5000', 'Professional Services', '45,500.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '45,500.00', '0.00'],
      ['01 5005', 'Security Guard Services', '135,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '135,000.00', '0.00'],
      [
        '01 5010',
        'SWPPP / Erosion Control',
        '247,323.00',
        '65,000.00',
        '30,000.00',
        '0.00',
        '95,000.00',
        '38.4%',
        '152,323.00',
        '9,500.00',
      ],
      [
        '01 5015',
        'Temporary Project Expenses',
        '1,857,915.00',
        '137,953.38',
        '20,440.43',
        '0.00',
        '158,393.81',
        '8.5%',
        '1,699,521.19',
        '15,839.38',
      ],
      [
        '01 7500',
        'Cleaning, Pressure Washing (Continuous & Final)',
        '180,350.00',
        '684.62',
        '0.00',
        '0.00',
        '684,62',
        '0,4%',
        '179,665.38',
        '68.46',
      ],
      ['03 0000', 'CONCRETE', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      [
        '03 3000',
        'Slab, Grade Beams, Piers/Footings (Building & Enclosures)',
        '1,257,100.00',
        '0.00',
        '75,142.00',
        '0.00',
        '75,142.00',
        '6.0%',
        '1,181,958.00',
        '7,514.20',
      ],
      ['03 3005', 'Fuel Canopy Footings', '250,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '250,000.00', '0.00'],
      ['03 3010', 'Paving Under Fuel Canopy', '725,550.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '725,550.00', '0.00'],
      ['03 3015', 'Paving Over Fuel Tanks', '90,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '90,000.00', '0.00'],
      [
        '03 3020',
        'All Other Site Paving Including Parking and Colored Concrete Bands',
        '4,681,076.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '4,681,076.00',
        '0.00',
      ],
      [
        'A ITEM NO.',
        'B DESCRIPTION OF WORK',
        'C SCHEDULED VALUE',
        'D WORK COMPLETED FROM PREVIOUS APPLICATION (D + E)',
        'E THIS PERIOD',
        'F MATERIALS PRESENTLY STORED (NOT IN DOR E)',
        'G TOTAL COMPLETED AND STORED TO DATE (D+E+F)',
        '% (G /C)',
        'H BALANCE TO FINISH (C - G)',
        'I RETAINAGE (IF VARIABLE RATE)',
      ],
      [
        '03 3025',
        'Curbs, Sidewalks incl. Grade Beams, Enclosures on Sidewalks, All Ramps, Statue Pedestals',
        '250,000.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '250,000.00',
        '0.00',
      ],
      ['03 3030', 'Light Pole Footings (Building)', '15,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '15,000.00', '0.00'],
      ['03 3035', 'Light Pole Footings (Site)', '80,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '80,000.00', '0.00'],
      ['03 3040', 'Bollards (Fuel Canopy Area)', '110,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '110,000.00', '0.00'],
      ['03 3045', 'Bollards (All Others)', '35,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '35,000.00', '0.00'],
      [
        '03 3050',
        'Miscellaneous Concrete Associated w/Storm Drainage',
        '45,000.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '45,000.00',
        '0.00',
      ],
      ['03 4713', 'Tilt Wall Fabrication & Erection', '755,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '755,000.00', '0.00'],
      ['04 0000', 'MASONRY', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['04 7200', 'Stone Work / Bullnose (Building)', '737,805.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '737,805.00', '0.00'],
      ['04 7205', 'Stone Work / Bullnose (Fuel Canopy)', '228,588.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '228,588.00', '0.00'],
      ['05 0000', 'METALS', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      [
        '05 1200',
        'Building Structural Steel & Erection (Including Trench Grating & Roof Ladder)',
        '1,587,592.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '1,587,592.00',
        '0.00',
      ],
      [
        '05 4000',
        'Cold-Formed Metal Stud Framing Interior/Exterior (Building)',
        '575,000.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '575,000.00',
        '0.00',
      ],
      ['05 4005', 'Cold-Formed Metal Stud Framing (Canopy)', '158,256.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '158,256.00', '0.00'],
      ['06 0000', 'WOOD, PLASTICS & COMPOSITES', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['06 0525', 'Slatwall Paneling System', '79,403.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '79,403.00', '0.00'],
      [
        '06 1000',
        'Fire Rated Plywood (Building Interior/Exterior)',
        '83,000.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '83,000.00',
        '0.00',
      ],
      ['06 1005', 'Fire Rated Plywood (Canopy)', '131,650.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '131,650.00', '0.00'],
      [
        '06 1015',
        'Shelving & Brackets, Clothes Rods & Brackets, PLAM Countertops & Brackets, Interior Wood Trim (Building)',
        '37,061.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '37,061.00',
        '0.00',
      ],
      [
        'A ITEM NO.',
        'B DESCRIPTION OF WORK',
        'C SCHEDULED VALUE',
        'D WORK COMPLETED FROM PREVIOUS APPLICATION (D + E)',
        'E THIS PERIOD',
        'F MATERIALS PRESENTLY STORED (NOT IN DOR E)',
        'G TOTAL COMPLETED AND STORED TO DATE (D+E+F)',
        '% (G /C)',
        'H BALANCE TO FINISH (C - G)',
        'I RETAINAGE (IF VARIABLE RATE)',
      ],
      [
        '06 4100',
        'Store Millwork & Solid Surface Countertops (Excludes Foodservice Millwork)',
        '544,646.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '544,646.00',
        '0.00',
      ],
      ['07 0000', 'THERMAL & MOISTURE PROTECTION', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['07 2400', 'Stucco/EIFS Moldings (Building)', '215,504.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '215,504.00', '0.00'],
      ['07 2405', 'Stucco/EIFS Moldings (Fuel Canopy)', '288,016.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '288,016.00', '0.00'],
      [
        '07 4000',
        'Roofing System (Single-Ply & Metal Roof Systems)',
        '873,203.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '873,203.00',
        '0.00',
      ],
      [
        '07 9200',
        'Interior/Exterior Caulking (Building & Site)',
        '386,525.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '386,525.00',
        '0.00',
      ],
      ['08 0000', 'OPENINGS', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      [
        '08 0671',
        'Interior/Exterior Finish Hardware, Locksmith Services Final Keying, Pad Locks for IDF Cabinets, Knox Boxes (Building)',
        '233,006.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '233,006.00',
        '0.00',
      ],
      ['08 3105', 'Access Doors (Fuel Canopies)', '9,500.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '9,500.00', '0.00'],
      ['08 3323', 'Overhead Coiling Doors (Building)', '207,214.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '207,214.00', '0.00'],
      ['08 4229', 'Automatic Sliding Glass Door w/Frame', '125,539.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '125,539.00', '0.00'],
      [
        '08 4313',
        'Interior Glass / Storefront Glass & Frames / Mirrors (Building)',
        '104,086.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '104,086.00',
        '0.00',
      ],
      ['09 0000', 'FINISHES', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      [
        '09 2116',
        'Drywall System / Cement Board / Sheathing (Building)',
        '1,475,000.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '1,475,000.00',
        '0.00',
      ],
      [
        '09 2117',
        'Drywall System / Cement Board / Sheathing (Canopy)',
        '165,000.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '165,000.00',
        '0.00',
      ],
      ['09 3000', 'Ceramic Tile System (Building)', '1,320,400.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '1,320,400.00', '0.00'],
      ['09 5100', 'Acoustical Ceiling System (Building)', '262,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '262,000.00', '0.00'],
      ['09 6119', 'Staining of Concrete Star', '85,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '85,000.00', '0.00'],
      [
        'A ITEM NO.',
        'B DESCRIPTION OF WORK',
        'C SCHEDULED VALUE',
        'D WORK COMPLETED FROM PREVIOUS APPLICATION (D + E)',
        'E THIS PERIOD',
        'F MATERIALS PRESENTLY STORED (NOT IN D OR E)',
        'G TOTAL COMPLETED AND STORED TO DATE (D+E+F)',
        '% (G / C)',
        'H BALANCE TO FINISH (C - G)',
        'I RETAINAGE (IF VARIABLE RATE)',
      ],
      [
        '09 9000',
        'Interior/Exterior/Site Painting - Including Doors, Roof Ladder, Manhole Covers at Fuel Tanks, Enclosure Gates, Access Doors (Building)',
        '114,942.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '114,942.00',
        '0.00',
      ],
      ['10 0000', 'SPECIALTIES', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      [
        '10 1400',
        'Signage Interior & Exterior (Building Logos, Channel Letters & Bird Spikes)',
        '47,397.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '47,397.00',
        '0.00',
      ],
      [
        '10 1405',
        'Signage (Fuel Canopy Logos, Channel Letters, Panel Signs & Bird Spikes)',
        '48,879.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '48,879.00',
        '0.00',
      ],
      ['10 1410', 'Pylon Sign Pole (materials & installation)', '264,258.15', '0.00', '0.00', '0.00', '0.00', '0.0%', '264,258.15', '0.00'],
      ['10 1415', 'Pylon Sign Logo (materials & installation)', '142,292.85', '0.00', '0.00', '0.00', '0.00', '0.0%', '142,292.85', '0.00'],
      ['10 2800', 'Grab Bars (Building)', '6,900.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '6,900.00', '0.00'],
      [
        '10 4400',
        'Fire Protection Specialties (Extinguishers & Cabinets)',
        '34,000.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '34,000.00',
        '0.00',
      ],
      ['10 7300', 'Fuel Canopies', '1,132,276.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '1,132,276.00', '0.00'],
      ['10 7305', 'LED Lighting for Fuel Canopies', '59,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '59,000.00', '0.00'],
      ['11 0000', 'EQUIPMENT', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['11 4000', 'Foodservice Equipment', '1,616,094.00', '110,571.62', '0.00', '0.00', '110,571.62', '6.8%', '1,505,522.38', '11,057.16'],
      [
        '11 4010',
        'Refrigeration Equipment',
        '1,256,941.00',
        '137,641.90',
        '0.00',
        '0.00',
        '137,641.90',
        '11.0%',
        '1,119,299.10',
        '13,764.19',
      ],
      ['21 0000', 'FIRE SUPRESSION', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['21 0100', 'Fire Sprinkler System', '661,685.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '661,685.00', '0.00'],
      ['22 0000', 'PLUMBING', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['22 0000', 'Sanitary Plumbing', '1,601,600.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '1,601,600.00', '0.00'],
      ['22 0005', 'Domestic Water Plumbing', '366,080.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '366,080.00', '0.00'],
      ['22 0010', 'Plumbing Fixtures', '160,160.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '160,160.00', '0.00'],
      ['22 0015', 'Gas Piping', '68,640.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '68,640.00', '0.00'],
      [
        'A ITEM NO.',
        'B DESCRIPTION OF WORK',
        'C SCHEDULED VALUE',
        'D WORK COMPLETED FROM PREVIOUS APPLICATION (D + E)',
        'E THIS PERIOD',
        'F MATERIALS PRESENTLY STORED (NOT IN D OR E)',
        'G TOTAL COMPLETED AND STORED TO DATE (D+E+F)',
        '% (G / C)',
        'H BALANCE TO FINISH (C - G)',
        'I RETAINAGE (IF VARIABLE RATE)',
      ],
      ['22 3116', 'Water Filtration Systems', '91,520.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '91,520.00', '0.00'],
      ['23 0000', 'HVAC', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['23 0000', 'HVAC Systems (Building)', '2,704,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '2,704,000.00', '0.00'],
      [
        '23 0933',
        'Building Management & Control Systems (BMCS)',
        '422,716.00',
        '0.00',
        '27,476.54',
        '0.00',
        '27,476.54',
        '6.5%',
        '395,239.46',
        '2,747.65',
      ],
      ['26 0000', 'ELECTRICAL', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      [
        '26 0000',
        'Building Electrical (excludes Surge Protection, Foodservice Electrical and Lighting)',
        '3,163,261.32',
        '240,000.00',
        '0.00',
        '45,000.00',
        '285,000.00',
        '9.0%',
        '2,878,261.32',
        '28,500.00',
      ],
      ['26 0005', 'Surge Protection', '92,087.39', '0.00', '0.00', '0.00', '0.00', '0.0%', '92,087.39', '0.00'],
      ['26 0010', 'Interior Lighting', '465,896.42', '0.00', '0.00', '10,000.00', '10,000.00', '2.1%', '455,896.42', '1,000.00'],
      [
        '26 0015',
        'Exterior Lighting (Parking Lot & Sidewalks)',
        '260,539.94',
        '0.00',
        '0.00',
        '10,000.00',
        '10,000.00',
        '3.8%',
        '250,539.94',
        '1,000.00',
      ],
      [
        '26 0020',
        'Exterior Lighting (Wall Packs)',
        '22,460.34',
        '0.00',
        '0.00',
        '10,000.00',
        '10,000.00',
        '44.5%',
        '12,460.34',
        '1,000.00',
      ],
      ['26 0025', 'Foodservice Electrical', '269,823.55', '0.00', '0.00', '0.00', '0.00', '0.0%', '269,823.55', '0.00'],
      ['26 0030', 'Site Electrical', '1,455,824.59', '50,000.00', '0.00', '0.00', '50,000.00', '3.4%', '1,405,824.59', '5,000.00'],
      [
        '26 0035',
        'Fuel Canopy Electrical',
        '1,545,945.20',
        '25,000.00',
        '0.00',
        '275,970.50',
        '300,970.50',
        '19.5%',
        '1,244,974.70',
        '30,097.05',
      ],
      ['26 0040', 'Fuel System Electrical', '139,029.50', '40,000.00', '0.00', '99,029.50', '139,029.50', '100.0%', '0.00', '13,902.95'],
      ['26 3100', 'Fire Alarm System', '121,911.75', '0.00', '0.00', '0.00', '0.00', '0.0%', '121,911.75', '0.00'],
      ['28 0000', 'LOW VOLTAGE', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['28 1800', 'Low Voltage', '1,287,180.00', '0.00', '0.00', '225,462.22', '225,462.22', '17.5%', '1,061,717.78', '22,546.22'],
      ['31 0000', 'EARTHWORK', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      [
        '31 0000',
        'Earthwork, Clearing, Grading, Excavation, Trenching, Fill, Soil Stabilization',
        '6,965,385.00',
        '1,269,000.00',
        '1,427,070.87',
        '0.00',
        '2,696,070.87',
        '38.7%',
        '4,269,314.13',
        '269,607.09',
      ],
      ['32 0000', 'EXTERIOR IMPROVEMENTS', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      [
        'A ITEM NO.',
        'B DESCRIPTION OF WORK',
        'C SCHEDULED VALUE',
        'D WORK COMPLETED FROM PREVIOUS APPLICATION (D + E)',
        'E THIS PERIOD',
        'F MATERIALS PRESENTLY STORED (NOT IN D OR E)',
        'G TOTAL COMPLETED AND STORED TO DATE (D+E+F)',
        '% (G / C)',
        'H BALANCE TO FINISH (C - G)',
        'I RETAINAGE (IF VARIABLE RATE)',
      ],
      [
        '32 1713',
        'Parking Lot Striping, ADA Signage, Traffic Control Signage & Wheel Stops',
        '238,913.00',
        '0.00',
        '0.00',
        '0.00',
        '0.00',
        '0.0%',
        '238,913.00',
        '0.00',
      ],
      ['32 3100', 'Site Fencing', '130,580.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '130,580.00', '0.00'],
      ['32 8000', 'Irrigation & Irrigation Backflow Device', '460,962.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '460,962.00', '0.00'],
      ['32 9000', 'Landscaping', '1,270,169.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '1,270,169.00', '0.00'],
      ['33 0000', 'Utilities', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['33 0000', 'Site Utilities', '4,189,391.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '4,189,391.00', '0.00'],
      ['50 0000', 'Fuel System', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      [
        '50 1000',
        'Fuel System Installation & Start-Up',
        '2,481,000.00',
        '0.00',
        '124,050.00',
        '0.00',
        '124,050.00',
        '5.0%',
        '2,356,950.00',
        '12,405.00',
      ],
      ['70 0000', 'ALLOWANCES', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      ['70 0001', 'Drawing Revision Allowance', '250,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '250,000.00', '0.00'],
      ['71 0002', 'Undercut Deep Foundations Allowance', '178,124.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '178,124.00', '0.00'],
      [
        '71 0003',
        'Undercut Building Pad Allowance',
        '158,194.00',
        '90,849.00',
        '0.00',
        '0.00',
        '90,849.00',
        '57.4%',
        '67,345.00',
        '9,084.90',
      ],
      ['71 0004', 'Temp Dewatering Allowance', '50,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '50,000.00', '0.00'],
      ['71 0005', 'Temp Traffic Light Allowance', '200,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '200,000.00', '0.00'],
      ['71 0006', 'DAS System Testing', '13,000.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '13,000.00', '0.00'],
      ['72 0000', 'Buyout Tracking', '0.00', '0.00', '0.00', '0.00', '0.00', '0.0%', '0.00', '0.00'],
      [
        '80 0000',
        "Contractor's Monthly Fixed Fee & Expenses",
        '3,717,102.00',
        '232,318.86',
        '232,318.88',
        '0.00',
        '464,637.74',
        '12.5%',
        '3,252,464.26',
        '46,463.78',
      ],
      [
        '',
        'GRAND TOTAL',
        '$61,019,904.00',
        '$2,819,954.38',
        '$1,936,498.72',
        '$675,462.22',
        '$5,431,915.32',
        '8.9%',
        '$55,587,988.68',
        '$543,191.53',
      ],
    ],
    headerMappings: {
      'G TOTAL COMPLETED AND STORED TO DATE (D+E+F)': 'completedStoredToDate',
      'C SCHEDULED VALUE': 'revisedBudget',
      'I RETAINAGE (IF VARIABLE RATE)': 'retainage',
      'D WORK COMPLETED FROM PREVIOUS APPLICATION (D + E)': 'previousApplication',
      'B DESCRIPTION OF WORK': 'description',
      'F MATERIALS PRESENTLY STORED (NOT IN DOR E)': 'storedThisPeriod',
      'E THIS PERIOD': 'completeThisPeriod',
    },
  },
];

export default SEEDED_PAYAPPS;
